import React from 'react';
import {
  Box,
  VStack,
  Icon,
  Text,
  useColorModeValue,
  Flex,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Divider,
  HStack,
  Avatar,
  Badge,
} from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiHome,
  FiDollarSign,
  FiUsers,
  FiPieChart,
  FiClock,
  FiBarChart2,
  FiSettings,
  FiHelpCircle,
  FiFileText,
  FiX,
  FiUser,
} from 'react-icons/fi';
import { useAccount } from 'wagmi';
import PriceDisplay from '@/components/common/PriceDisplay';
import Logo from '@/components/common/Logo';

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItemProps {
  to: string;
  icon: React.ElementType;
  children: React.ReactNode;
  onClick?: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon, children, onClick }) => {
  const activeBg = useColorModeValue('brand.50', 'brand.900');
  const activeColor = useColorModeValue('brand.600', 'brand.200');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');

  return (
    <NavLink to={to} onClick={onClick}>
      {({ isActive }) => (
        <Flex
          align="center"
          p={4}
          mx={2}
          borderRadius="lg"
          role="group"
          cursor="pointer"
          bg={isActive ? activeBg : 'transparent'}
          color={isActive ? activeColor : 'inherit'}
          _hover={{
            bg: isActive ? activeBg : hoverBg,
          }}
          transition="all 0.2s"
        >
          <Box
            mr={4}
            fontSize="18"
            as={icon}
            color={isActive ? activeColor : 'inherit'}
          />
          <Text fontWeight={isActive ? 'semibold' : 'medium'} fontSize="md">
            {children}
          </Text>
        </Flex>
      )}
    </NavLink>
  );
};

const MobileNav: React.FC<MobileNavProps> = ({ isOpen, onClose }) => {
  const { address, isConnected } = useAccount();
  const { t } = useTranslation('common');
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const navItems = [
    { to: '/', icon: FiHome, label: t('navigation.dashboard') },
    { to: '/recharge', icon: FiDollarSign, label: t('navigation.recharge') },
    { to: '/invite', icon: FiUsers, label: t('navigation.invite') },
    { to: '/management', icon: FiPieChart, label: t('navigation.management') },
    { to: '/history', icon: FiClock, label: t('navigation.history') },
    { to: '/reserve', icon: FiBarChart2, label: t('navigation.reserve') },
    { to: '/whitepaper', icon: FiFileText, label: t('navigation.whitepaper') },
    { to: '/settings', icon: FiSettings, label: t('navigation.settings') },
    { to: '/help', icon: FiHelpCircle, label: t('navigation.help') },
  ];

  return (
    <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="full">
      <DrawerOverlay />
      <DrawerContent bg={bg}>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor={borderColor}>
          <VStack spacing={3} align="stretch">
            <Box display="flex" alignItems="center" gap={2}>
              <Logo size="sm" showText={true} />
            </Box>
            
            {/* 价格显示 */}
            <Box>
              <PriceDisplay 
                context="mobile-nav"
                showLink={false}
                variant="minimal"
                showWarning={true}
              />
            </Box>

            {/* 用户状态 */}
            <HStack spacing={3}>
              <Avatar size="sm" icon={<FiUser />} />
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" fontWeight="medium">
                  {isConnected ? t('navigation.connectedWallet') : t('navigation.disconnectedWallet')}
                </Text>
                {isConnected && address && (
                  <Text fontSize="xs" color="gray.500" fontFamily="mono">
                    {address.slice(0, 6)}...{address.slice(-4)}
                  </Text>
                )}
              </VStack>
              {isConnected && (
                <Badge colorScheme="green" size="sm">{t('success.connected')}</Badge>
              )}
            </HStack>
          </VStack>
        </DrawerHeader>

        <DrawerBody p={0}>
          <VStack spacing={1} align="stretch" pt={2}>
            {navItems.map((item) => (
              <NavItem 
                key={item.to} 
                to={item.to} 
                icon={item.icon}
                onClick={onClose}
              >
                {item.label}
              </NavItem>
            ))}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default MobileNav; 